import { useState, type ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import { adminApi, AdminApiError } from '../../lib/adminApi'

// Platform Ops: the two phase 1 billing backfills (docs/rfc/billing.md 11.1 and
// 5.10), run once per pool. Each card does two things and nothing else:
//
//   Preview  runs the backfill as a dry run and shows what it would do.
//   Apply    runs it for real. It stays disabled until a preview has run in this
//            dialog, shows that preview's counts in a confirmation, and needs the
//            pool's name typed exactly before it goes ahead.
//
// The pool name comes from the server's own report, not from this page, so what
// has to be typed is what the API says it is. If the server could not work it
// out, Apply is unavailable rather than guessed.

export interface BillingSourceBackfillRow {
  slug: string
  isActive: boolean | null
  deprovisioned: boolean
  current: string | null
  action: 'set_exempt' | 'skip_has_value' | 'skip_invalid_slug'
  result?: 'written' | 'skipped_no_row' | 'skipped_has_value' | 'skipped_concurrent' | 'failed'
  error?: string
  audited?: boolean
  auditError?: string
}

export interface BillingSourceBackfillReport {
  dryRun: boolean
  pool: string | null
  auditTableReady: boolean
  rows: BillingSourceBackfillRow[]
  summary: { total: number; toSet: number; set: number; skipped: number; failed: number; auditFailed: number }
}

export interface ScopeUsageTenantReport {
  slug: string
  schemaName: string
  status: 'ok' | 'skipped_no_schema' | 'skipped_no_audit_log' | 'failed'
  auditScopes: number
  alreadyCounted: number
  toInsert: number
  inserted: number | null
  byMonth: Record<string, number>
  usageRowsBefore: number
  usageRowsAfter: number
  reconciled: boolean
  error?: string
}

export interface ScopeUsageBackfillReport {
  dryRun: boolean
  pool: string | null
  tenants: ScopeUsageTenantReport[]
  summary: {
    tenants: number
    ok: number
    skipped: number
    failed: number
    auditScopes: number
    toInsert: number
    inserted: number | null
    byMonth: Record<string, number>
  }
  reconciliation: { checked: number; matched: number; mismatched: string[] }
}

interface Report {
  dryRun: boolean
  pool: string | null
}

interface CardConfig<T extends Report> {
  title: string
  description: ReactNode
  path: string
  // What Apply would change, and the counts the confirmation shows.
  toApply: (report: T) => number
  confirmLines: (report: T) => Array<[string, string | number]>
  // Why Apply is unavailable for a preview that otherwise has work in it.
  blocker?: (report: T) => string | null
  renderDetails: (report: T) => ReactNode
  renderResult: (report: T) => ReactNode
}

function errorText(err: unknown): string {
  return err instanceof AdminApiError ? err.message : 'The request failed'
}

function SummaryList({ lines }: { lines: Array<[string, string | number]> }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 text-sm">
      {lines.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="font-medium text-foreground">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function ConfirmApply<T extends Report>({
  title,
  report,
  lines,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  title: string
  report: T
  lines: Array<[string, string | number]>
  pending: boolean
  error: string | null
  onCancel: () => void
  onConfirm: () => void
}) {
  const [typed, setTyped] = useState('')
  const pool = report.pool
  const matches = pool !== null && typed === pool

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
      <div role="dialog" aria-modal="true" aria-label={`Confirm apply: ${title}`} className="w-full max-w-md space-y-4 rounded-lg bg-background p-6 shadow-lg">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">Apply: {title}</h2>
          <p className="text-sm text-muted-foreground">
            {pool === null
              ? 'This pool could not be identified, so it cannot be applied from here.'
              : <>This writes to the <strong>{pool}</strong> pool. What the preview found:</>}
          </p>
        </div>

        <SummaryList lines={lines} />

        {pool !== null && (
          <label className="block space-y-1 text-sm">
            <span className="text-foreground">Type <code className="font-mono">{pool}</code> to confirm</span>
            <input
              aria-label="Type the pool name to confirm"
              value={typed}
              onChange={e => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm"
            />
          </label>
        )}

        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <button onClick={onCancel} disabled={pending} className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!matches || pending}
            className="rounded-md bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground hover:opacity-90 disabled:opacity-50"
          >
            {pending ? 'Applying…' : 'Confirm apply'}
          </button>
        </div>
      </div>
    </div>
  )
}

function BackfillCard<T extends Report>({ config }: { config: CardConfig<T> }) {
  const [preview, setPreview] = useState<T | null>(null)
  const [result, setResult] = useState<T | null>(null)
  const [confirming, setConfirming] = useState(false)

  const runPreview = useMutation({
    mutationFn: () => adminApi.post<{ data: T }>(config.path, { dryRun: true }),
    onSuccess: res => {
      setPreview(res.data)
      setResult(null)
    },
  })

  const runApply = useMutation({
    mutationFn: () => adminApi.post<{ data: T }>(config.path, { dryRun: false }),
    onSuccess: res => {
      setResult(res.data)
      // The data has changed, so the preview that justified this apply is spent:
      // applying again needs a fresh one.
      setPreview(null)
      setConfirming(false)
    },
  })

  const workToDo = preview ? config.toApply(preview) : 0
  const blocker = preview ? config.blocker?.(preview) ?? null : null
  let applyBlockedBy: string | null = null
  if (!preview) applyBlockedBy = 'Run a preview first.'
  else if (blocker) applyBlockedBy = blocker
  else if (workToDo === 0) applyBlockedBy = 'Nothing to apply.'

  return (
    <section aria-label={config.title} className="space-y-3 rounded-lg border border-border p-4">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-foreground">{config.title}</h3>
        <p className="text-sm text-muted-foreground">{config.description}</p>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => runPreview.mutate()}
          disabled={runPreview.isPending || runApply.isPending}
          className="rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted disabled:opacity-50"
        >
          {runPreview.isPending ? 'Previewing…' : 'Preview'}
        </button>
        <button
          onClick={() => { runApply.reset(); setConfirming(true) }}
          disabled={applyBlockedBy !== null || runApply.isPending}
          className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          Apply
        </button>
        {applyBlockedBy && <span className="text-xs text-muted-foreground">{applyBlockedBy}</span>}
      </div>

      {runPreview.isError && <p role="alert" className="text-sm text-destructive">{errorText(runPreview.error)}</p>}

      {preview && (
        <div className="space-y-2" aria-label="Preview">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Preview (nothing written)</p>
          {config.renderDetails(preview)}
        </div>
      )}

      {result && (
        <div className="space-y-2" aria-label="Result">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Applied</p>
          {config.renderResult(result)}
        </div>
      )}

      {confirming && preview && (
        <ConfirmApply
          title={config.title}
          report={preview}
          lines={config.confirmLines(preview)}
          pending={runApply.isPending}
          error={runApply.isError ? errorText(runApply.error) : null}
          onCancel={() => setConfirming(false)}
          onConfirm={() => runApply.mutate()}
        />
      )}
    </section>
  )
}

const tableClasses = 'w-full text-left text-xs'
const scrollClasses = 'max-h-64 overflow-y-auto rounded-md border border-border'

function rowState(row: BillingSourceBackfillRow): string {
  if (row.deprovisioned) return 'Torn down'
  if (row.isActive === false) return 'Suspended'
  if (row.isActive === true) return 'Active'
  return 'Unknown'
}

const PLAN_TEXT: Record<BillingSourceBackfillRow['action'], string> = {
  set_exempt: 'Set to exempt',
  skip_has_value: 'Skip: already has a value',
  skip_invalid_slug: 'Skip: not a valid tenant slug',
}

function resultText(row: BillingSourceBackfillRow): string {
  switch (row.result) {
    case 'written': return row.audited === false ? 'Set to exempt (audit row NOT written)' : 'Set to exempt'
    case 'skipped_concurrent': return 'Skipped: changed while running'
    case 'skipped_has_value': return 'Skipped: already has a value'
    case 'skipped_no_row': return 'Skipped: row is gone'
    case 'failed': return `Failed: ${row.error ?? 'unknown error'}`
    default: return PLAN_TEXT[row.action]
  }
}

function BillingSourceTable({ report, showResult }: { report: BillingSourceBackfillReport; showResult: boolean }) {
  return (
    <div className={scrollClasses}>
      <table className={tableClasses}>
        <thead className="sticky top-0 bg-muted text-muted-foreground">
          <tr><th className="px-3 py-1.5">Tenant</th><th className="px-3 py-1.5">State</th><th className="px-3 py-1.5">Current</th><th className="px-3 py-1.5">{showResult ? 'Result' : 'Plan'}</th></tr>
        </thead>
        <tbody>
          {report.rows.map(row => (
            <tr key={row.slug} className="border-t border-border">
              <td className="px-3 py-1.5 font-mono">{row.slug}</td>
              <td className="px-3 py-1.5">{rowState(row)}</td>
              <td className="px-3 py-1.5">{row.current ?? 'none'}</td>
              <td className="px-3 py-1.5">{showResult ? resultText(row) : PLAN_TEXT[row.action]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const BILLING_SOURCE_CARD: CardConfig<BillingSourceBackfillReport> = {
  title: 'billingSource backfill',
  description: (
    <>
      Gives every registry row that has no billingSource an explicit <code>exempt</code>, so no tenant relies on the
      fail-open default. A row that already has any value is never touched. Applying it in each pool is a precondition
      of the shadow-mode slice.
    </>
  ),
  path: '/admin/billing/backfill/billing-source',
  toApply: report => report.summary.toSet,
  blocker: report => (report.auditTableReady ? null : 'The audit table is missing: run Migrate pool first.'),
  confirmLines: report => [
    ['Rows in the registry', report.summary.total],
    ['Will be set to exempt', report.summary.toSet],
    ['Will be skipped', report.summary.skipped],
  ],
  renderDetails: report => (
    <>
      <SummaryList lines={[['Rows', report.summary.total], ['Would set to exempt', report.summary.toSet], ['Would skip', report.summary.skipped]]} />
      <BillingSourceTable report={report} showResult={false} />
    </>
  ),
  renderResult: report => (
    <>
      <SummaryList
        lines={[['Rows', report.summary.total], ['Set to exempt', report.summary.set], ['Skipped', report.summary.skipped], ['Failed', report.summary.failed], ['Audit rows missing', report.summary.auditFailed]]}
      />
      <BillingSourceTable report={report} showResult />
    </>
  ),
}

function monthsText(byMonth: Record<string, number>): string {
  const entries = Object.entries(byMonth)
  return entries.length === 0 ? 'none' : entries.map(([month, n]) => `${month}: ${n}`).join(', ')
}

const STATUS_TEXT: Record<ScopeUsageTenantReport['status'], string> = {
  ok: 'OK',
  skipped_no_schema: 'Skipped: no schema',
  skipped_no_audit_log: 'Skipped: no audit log',
  failed: 'Failed',
}

function ScopeUsageTable({ report, showResult }: { report: ScopeUsageBackfillReport; showResult: boolean }) {
  return (
    <>
      <div className={scrollClasses}>
        <table className={tableClasses}>
          <thead className="sticky top-0 bg-muted text-muted-foreground">
            <tr>
              <th className="px-3 py-1.5">Tenant</th>
              <th className="px-3 py-1.5">Status</th>
              <th className="px-3 py-1.5">Completed scopes</th>
              <th className="px-3 py-1.5">Already counted</th>
              <th className="px-3 py-1.5">{showResult ? 'Inserted' : 'To insert'}</th>
              <th className="px-3 py-1.5">By month (UTC)</th>
              <th className="px-3 py-1.5">Reconciled</th>
            </tr>
          </thead>
          <tbody>
            {report.tenants.map(t => (
              <tr key={t.slug} className="border-t border-border">
                <td className="px-3 py-1.5 font-mono">{t.slug}</td>
                <td className="px-3 py-1.5">{t.status === 'failed' ? `Failed: ${t.error ?? 'unknown error'}` : STATUS_TEXT[t.status]}</td>
                <td className="px-3 py-1.5">{t.status === 'ok' ? t.auditScopes : '-'}</td>
                <td className="px-3 py-1.5">{t.status === 'ok' ? t.alreadyCounted : '-'}</td>
                <td className="px-3 py-1.5">{t.status === 'ok' ? (showResult ? t.inserted ?? 0 : t.toInsert) : '-'}</td>
                <td className="px-3 py-1.5">{t.status === 'ok' ? monthsText(t.byMonth) : '-'}</td>
                <td className="px-3 py-1.5">{t.status === 'ok' ? (t.reconciled ? 'Yes' : 'NO') : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Reconciliation: {report.reconciliation.matched} of {report.reconciliation.checked} tenants match their audit log
        {report.reconciliation.mismatched.length > 0 && <> (mismatch: {report.reconciliation.mismatched.join(', ')})</>}.
      </p>
    </>
  )
}

const SCOPE_USAGE_CARD: CardConfig<ScopeUsageBackfillReport> = {
  title: 'Scope usage backfill',
  description: (
    <>
      Copies each tenant&apos;s completed scopes from its audit log into the billing-grade scope count, dated at the
      first completion. It only adds what is missing and never changes a row that exists. Rows backfilled from before
      the completion audit row existed may land a month early.
    </>
  ),
  path: '/admin/billing/backfill/scope-usage',
  toApply: report => report.summary.toInsert,
  confirmLines: report => [
    ['Tenants', report.summary.tenants],
    ['Rows to insert', report.summary.toInsert],
    ['Months', monthsText(report.summary.byMonth)],
    ['Reconciliation after', `${report.reconciliation.matched} of ${report.reconciliation.checked} match`],
  ],
  renderDetails: report => (
    <>
      <SummaryList lines={[['Tenants', report.summary.tenants], ['Would insert', report.summary.toInsert], ['Skipped', report.summary.skipped], ['Failed', report.summary.failed]]} />
      <ScopeUsageTable report={report} showResult={false} />
    </>
  ),
  renderResult: report => (
    <>
      <SummaryList lines={[['Tenants', report.summary.tenants], ['Inserted', report.summary.inserted ?? 0], ['Skipped', report.summary.skipped], ['Failed', report.summary.failed]]} />
      <ScopeUsageTable report={report} showResult />
    </>
  ),
}

export function BillingBackfillsDialog({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div role="dialog" aria-modal="true" aria-label="Billing backfills" className="max-h-[90vh] w-full max-w-3xl space-y-4 overflow-y-auto rounded-lg bg-background p-6 shadow-lg">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">Billing backfills</h2>
          <p className="text-sm text-muted-foreground">
            One-time, once per pool. Preview shows what would happen and writes nothing. Apply needs a preview first and
            the pool&apos;s name typed to confirm. Both are safe to run again.
          </p>
        </div>

        <BackfillCard config={BILLING_SOURCE_CARD} />
        <BackfillCard config={SCOPE_USAGE_CARD} />

        <div className="flex justify-end">
          <button onClick={onClose} className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted">Close</button>
        </div>
      </div>
    </div>
  )
}
